/**
 * PinnedSkills
 *
 * Compact panel for managing skills pinned to a workproduct.
 * Shows pinned skills as chips with unpin button, and a "Browse Skills"
 * button that opens a modal to search, preview, star, and pin skills.
 */

import React, { useState, useEffect, useCallback } from "react";
import { Button, Modal, Form, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { useUserContext } from "../../Context/UserContext";
import type { SkillTemplateData } from "../../SkillsShowcase/SkillCard";
import "./PinnedSkills.css";

const backendUrl =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

interface PinnedSkillsProps {
  workproductId: string;
  readOnly?: boolean;
}

const PinnedSkills: React.FC<PinnedSkillsProps> = ({
  workproductId,
  readOnly,
}) => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const [pinnedSkills, setPinnedSkills] = useState<SkillTemplateData[]>([]);
  const [showBrowseModal, setShowBrowseModal] = useState(false);
  const [gallerySkills, setGallerySkills] = useState<SkillTemplateData[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const [showMySkills, setShowMySkills] = useState(false);
  // Skill preview: which skill is being viewed in detail
  const [previewSkill, setPreviewSkill] = useState<SkillTemplateData | null>(
    null
  );

  // Fetch pinned skills on mount
  const fetchPinnedSkills = useCallback(async () => {
    try {
      const res = await fetch(
        `${backendUrl}/api/workproducts/${workproductId}/pinned-skills`,
        { credentials: "include" }
      );
      if (res.ok) {
        const data = await res.json();
        setPinnedSkills(data);
      } else {
        console.error(
          "Fetch pinned skills failed:",
          res.status,
          await res.text()
        );
      }
    } catch (err) {
      console.error("Failed to fetch pinned skills:", err);
    }
  }, [workproductId]);

  useEffect(() => {
    fetchPinnedSkills();
  }, [fetchPinnedSkills]);

  // Fetch gallery skills for browse modal
  const fetchGallerySkills = useCallback(async (search?: string) => {
    setLoadingGallery(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      const res = await fetch(
        `${backendUrl}/api/skill-templates?${params.toString()}`,
        { credentials: "include" }
      );
      if (res.ok) {
        const data = await res.json();
        setGallerySkills(data);
      }
    } catch (err) {
      console.error("Failed to fetch gallery skills:", err);
    } finally {
      setLoadingGallery(false);
    }
  }, []);

  const handleOpenBrowse = () => {
    setShowBrowseModal(true);
    setPreviewSkill(null);
    fetchGallerySkills();
  };


  const handlePin = async (skill: SkillTemplateData) => {
    setPendingIds(prev => new Set(prev).add(skill.id));
    try {
      const res = await fetch(
        `${backendUrl}/api/workproducts/${workproductId}/pinned-skills`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ skillTemplateId: skill.id }),
        }
      );
      if (res.ok) {
        setPinnedSkills(prev => {
          if (prev.some(s => s.id === skill.id)) return prev;
          return [...prev, skill];
        });
      } else {
        console.error("Pin skill failed:", res.status, await res.text());
      }
    } catch (err) {
      console.error("Failed to pin skill:", err);
    } finally {
      setPendingIds(prev => {
        const next = new Set(prev);
        next.delete(skill.id);
        return next;
      });
    }
  };

  const handleUnpin = async (skillId: string) => {
    setPendingIds(prev => new Set(prev).add(skillId));
    try {
      const res = await fetch(
        `${backendUrl}/api/workproducts/${workproductId}/pinned-skills/${skillId}`,
        { method: "DELETE", credentials: "include" }
      );
      if (res.ok) {
        setPinnedSkills(prev => prev.filter(s => s.id !== skillId));
      } else {
        console.error("Unpin skill failed:", res.status, await res.text());
      }
    } catch (err) {
      console.error("Failed to unpin skill:", err);
    } finally {
      setPendingIds(prev => {
        const next = new Set(prev);
        next.delete(skillId);
        return next;
      });
    }
  };

  const handleStar = async (skillId: string) => {
    try {
      const res = await fetch(
        `${backendUrl}/api/skill-templates/${skillId}/star`,
        { method: "POST", credentials: "include" }
      );
      if (res.ok) {
        setGallerySkills(prev =>
          prev.map(s =>
            s.id === skillId
              ? { ...s, isStarred: true, starCount: (s.starCount || 0) + 1 }
              : s
          )
        );
      }
    } catch (err) {
      console.error("Failed to star skill:", err);
    }
  };

  const handleUnstar = async (skillId: string) => {
    try {
      const res = await fetch(
        `${backendUrl}/api/skill-templates/${skillId}/star`,
        { method: "DELETE", credentials: "include" }
      );
      if (res.ok) {
        setGallerySkills(prev =>
          prev.map(s =>
            s.id === skillId
              ? {
                  ...s,
                  isStarred: false,
                  starCount: Math.max(0, (s.starCount || 1) - 1),
                }
              : s
          )
        );
      }
    } catch (err) {
      console.error("Failed to unstar skill:", err);
    }
  };

  const pinnedIds = new Set(pinnedSkills.map(s => s.id));

  // Filter gallery by "My Skills" toggle and search query (client-side)
  const displayedSkills = gallerySkills.filter(s => {
    if (showMySkills && !(s.authorId && String(s.authorId) === String(user?.id))) {
      return false;
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return (
        s.name.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.tags.some(tag => tag.toLowerCase().includes(q))
      );
    }
    return true;
  });

  if (readOnly) return null;

  return (
    <>
      {/* Compact inline section */}
      <div className="pinned-skills-section">
        <div
          className="pinned-skills-header"
          onClick={() => setExpanded(!expanded)}
        >
          <span className="pinned-skills-label">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M4.146.146A.5.5 0 0 1 4.5 0h7a.5.5 0 0 1 .5.5c0 .68-.342 1.174-.646 1.479-.126.125-.25.224-.354.298v4.431l.078.048c.203.127.476.314.751.555C12.36 7.775 13 8.527 13 9.5a.5.5 0 0 1-.5.5h-4v4.5a.5.5 0 0 1-1 0V10h-4A.5.5 0 0 1 3 9.5c0-.973.64-1.725 1.17-2.189A6 6 0 0 1 5 6.708V2.277a3 3 0 0 1-.354-.298C4.342 1.674 4 1.18 4 .5a.5.5 0 0 1 .146-.354z" />
            </svg>
            {t("skills.pinnedSkills", "Skills")}
            {pinnedSkills.length > 0 && (
              <span className="pinned-skills-count">
                {pinnedSkills.length}
              </span>
            )}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="10"
            height="10"
            fill="currentColor"
            viewBox="0 0 16 16"
            className={`pinned-skills-chevron ${expanded ? "expanded" : ""}`}
          >
            <path d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
          </svg>
        </div>

        {expanded && (
          <div className="pinned-skills-body">
            {pinnedSkills.length === 0 ? (
              <div className="pinned-skills-empty">
                {t("skills.noPinnedSkills", "No skills pinned yet")}
              </div>
            ) : (
              <div className="pinned-skills-chips">
                {pinnedSkills.map(skill => (
                  <div key={skill.id} className="pinned-skill-chip">
                    <span className="pinned-skill-chip-name">
                      {skill.name}
                    </span>
                    <button
                      className="pinned-skill-chip-remove"
                      onClick={() => handleUnpin(skill.id)}
                      disabled={pendingIds.has(skill.id)}
                      title={t("skills.unpinSkill", "Unpin skill")}
                    >
                      {pendingIds.has(skill.id) ? (
                        <Spinner
                          animation="border"
                          size="sm"
                          style={{ width: 10, height: 10 }}
                        />
                      ) : (
                        "\u00d7"
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleOpenBrowse}
              className="pinned-skills-browse-btn"
            >
              + {t("skills.browseSkills", "Browse Skills")}
            </Button>
          </div>
        )}
      </div>

      {/* Browse Skills Modal */}
      <Modal
        show={showBrowseModal}
        onHide={() => {
          setShowBrowseModal(false);
          setPreviewSkill(null);
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {previewSkill
              ? previewSkill.name
              : t("skills.browseSkills", "Browse Skills")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* ── Skill Preview Mode ── */}
          {previewSkill ? (
            <div className="skill-preview">
              <div className="skill-preview-meta">
                <span className="browse-skill-card-category">
                  {previewSkill.category}
                </span>
                {previewSkill.jurisdiction && (
                  <span className="browse-skill-card-jurisdiction">
                    {previewSkill.jurisdiction}
                  </span>
                )}
                {previewSkill.authorName && (
                  <span className="skill-preview-author">
                    {previewSkill.authorName}
                  </span>
                )}
              </div>
              <p className="skill-preview-description">
                {previewSkill.description}
              </p>
              <div className="skill-preview-content">
                <ReactMarkdown>{previewSkill.content}</ReactMarkdown>
              </div>
              <div className="skill-preview-actions">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setPreviewSkill(null)}
                >
                  {t("skills.backToList", "Back")}
                </Button>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={() =>
                      previewSkill.isStarred
                        ? handleUnstar(previewSkill.id)
                        : handleStar(previewSkill.id)
                    }
                  >
                    {previewSkill.isStarred ? "\u2605" : "\u2606"}{" "}
                    {previewSkill.starCount || 0}
                  </Button>
                  <Button
                    variant={
                      pinnedIds.has(previewSkill.id)
                        ? "outline-danger"
                        : "primary"
                    }
                    size="sm"
                    disabled={pendingIds.has(previewSkill.id)}
                    onClick={() =>
                      pinnedIds.has(previewSkill.id)
                        ? handleUnpin(previewSkill.id)
                        : handlePin(previewSkill)
                    }
                  >
                    {pendingIds.has(previewSkill.id) ? (
                      <Spinner
                        animation="border"
                        size="sm"
                        style={{ width: 14, height: 14 }}
                      />
                    ) : pinnedIds.has(previewSkill.id) ? (
                      t("skills.unpinSkill", "Unpin")
                    ) : (
                      t("skills.pinSkill", "Pin")
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* ── Search + My Skills toggle ── */}
              <div className="browse-skills-toolbar">
                <Form.Control
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder={t(
                    "skills.searchPlaceholder",
                    "Search skills..."
                  )}
                  className="browse-skills-search-input"
                />
                <Button
                  variant={showMySkills ? "primary" : "outline-secondary"}
                  size="sm"
                  onClick={() => setShowMySkills(!showMySkills)}
                  className="browse-skills-my-toggle"
                >
                  {t("skills.mySkills", "My Skills")}
                </Button>
              </div>

              {/* ── Skills Grid ── */}
              {loadingGallery ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : (
                <div className="browse-skills-grid">
                  {displayedSkills.map(skill => {
                    const isPinned = pinnedIds.has(skill.id);
                    const isPending = pendingIds.has(skill.id);
                    return (
                      <div
                        key={skill.id}
                        className={`browse-skill-card ${isPinned ? "pinned" : ""}`}
                      >
                        {/* Clickable area for preview */}
                        <div
                          className="browse-skill-card-clickable"
                          onClick={() => setPreviewSkill(skill)}
                        >
                          <div className="browse-skill-card-top">
                            <span className="browse-skill-card-category">
                              {skill.category}
                            </span>
                            {skill.jurisdiction && (
                              <span className="browse-skill-card-jurisdiction">
                                {skill.jurisdiction}
                              </span>
                            )}
                          </div>
                          <div className="browse-skill-card-name">
                            {skill.name}
                          </div>
                          <div className="browse-skill-card-description">
                            {skill.description}
                          </div>
                        </div>
                        <div className="browse-skill-card-footer">
                          <div className="browse-skill-card-left">
                            <button
                              className={`browse-skill-star-btn ${skill.isStarred ? "starred" : ""}`}
                              onClick={() =>
                                skill.isStarred
                                  ? handleUnstar(skill.id)
                                  : handleStar(skill.id)
                              }
                              title={
                                skill.isStarred
                                  ? t("skills.unstar", "Unstar")
                                  : t("skills.star", "Star")
                              }
                            >
                              {skill.isStarred ? "\u2605" : "\u2606"}{" "}
                              {skill.starCount || 0}
                            </button>
                            <div className="browse-skill-card-tags">
                              {skill.tags.slice(0, 2).map(tag => (
                                <span
                                  key={tag}
                                  className="browse-skill-card-tag"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                          <Button
                            variant={
                              isPinned ? "outline-danger" : "outline-primary"
                            }
                            size="sm"
                            disabled={isPending}
                            onClick={() =>
                              isPinned
                                ? handleUnpin(skill.id)
                                : handlePin(skill)
                            }
                          >
                            {isPending ? (
                              <Spinner
                                animation="border"
                                size="sm"
                                style={{ width: 14, height: 14 }}
                              />
                            ) : isPinned ? (
                              t("skills.unpinSkill", "Unpin")
                            ) : (
                              t("skills.pinSkill", "Pin")
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  {displayedSkills.length === 0 && (
                    <div className="text-center text-muted py-3">
                      {t("skills.noSkillsFound", "No skills found")}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default PinnedSkills;
